<template>
  <div v-if="shouldDisplayGallery" :class="CAROUSEL_MAX_HEIGHT_CLASS">
    <Carousel
      data-test="carousel"
      :items="imagesListWithExtraItems"
      primary-image-wrapper-classes="pl-24"
      :product-name="trackingProductModel"
      :secondary-list-classes="CAROUSEL_MAX_HEIGHT_CLASS"
      :tracking="{ name: 'product', zone: 'pp_carousel' }"
      @scroll-end="onScrollEnd"
      @scroll-start="onScrollStart"
    >
      <template #first="{ items, productName }">
        <MainImageContainer
          ref="mainImageContainer"
          :active-index="currentActiveIndex || 0"
          :allow-media-viewer
          :images="items"
          :is-mobile
          :product-name
          :review="review ?? null"
          :with-sticker="shouldDisplaySticker"
          @click="openMediaViewer"
          @extra-item-click="trackExtraItemClick"
          @sticker-click="emit('sticker-click')"
          @visible="(index, type) => onVisibleImage(index, type)"
        >
          <template #sticker="{ size }">
            <slot name="sticker" :size />
          </template>
        </MainImageContainer>
      </template>
      <template #rest="{ items, productName }">
        <template v-for="(image, index) in items" :key="image.id || image.src">
          <AltImageItem
            :allow-media-viewer
            class="md:[order:var(--md-order)]"
            :class="{
              // hide on mobile the extra items
              'hidden md:block':
                (image.isExtra &&
                  image.type !== 'jpRefurbishmentInfographic') ||
                index === 0,
              'relative after:bg-overlay-low after:rounded-lg after:pointer-events-none after:absolute after:inset-0 after:z-[1] after:h-full after:w-full':
                image.type === 'video',
            }"
            :data-index="index"
            :extra-items-indexes
            :image
            :index
            :is-mobile
            :product-name
            :style="{
              '--md-order': image.desktopOrder || 'initial',
            }"
            :total-length="imagesListWithExtraItems.length"
            :with-sticker="index === 0 && shouldDisplaySticker"
            v-bind="getAltImageEvents(image, index)"
          >
            <template #sticker="{ size }">
              <slot name="sticker" :size />
            </template>
            <IconMediaPlay
              v-if="image.isExtra && image.type === 'video'"
              class="absolute bottom-8 left-8 z-[2] fill-[#FFF]"
              size="48"
            />
          </AltImageItem>
          <ExtraItem
            v-if="image.isExtra && image.type === 'review'"
            :key="`review-${image.id || image.src}`"
            :allow-media-viewer="false"
            class="md:[order:var(--md-order)]"
            :class="{
              // show on mobile
              'block md:hidden': image.isExtra,
            }"
            :data-index="index"
            :index
            :item-size-class="BIG_ITEM_SIZE_CLASS"
            :style="{
              '--md-order': image.desktopOrder || 'initial',
            }"
          >
            <!-- Keep a stable element in the slot to avoid SSR/CSR shape mismatches -->
            <div class="h-full w-full" :class="BIG_ITEM_SIZE_CLASS">
              <Review
                v-if="review"
                :key="`review-component-${review.id}`"
                ref="reviewComponent"
                :aria-label="reviewAriaLabel"
                :card-class-name="BIG_ITEM_SIZE_CLASS"
                class="h-full w-full"
                :review
                @visible="onVisibleImage(index, 'review')"
              />
            </div>
          </ExtraItem>
          <ExtraItem
            v-if="image.isExtra && image.type === 'video'"
            :key="image.id || image.src"
            :allow-media-viewer="false"
            class="md:[order:var(--md-order)]"
            :class="{
              // show on mobile
              'block md:hidden': image.isExtra,
            }"
            :data-index="index"
            :index
            :item-size-class="BIG_ITEM_SIZE_CLASS"
            static
            :style="{
              '--md-order': image.desktopOrder || 'initial',
            }"
          >
            <MarketingVideo
              v-if="videoSource.isSelfHosted"
              :class="BIG_ITEM_SIZE_CLASS"
              :img-class-name="BIG_ITEM_SIZE_CLASS"
              :is-mobile
              :poster="videoSource.poster"
              :product-name="trackingProductModel"
              :sources="videoSource.sources"
              :title="videoSource.title"
              @start="onVisibleImage(index, 'video')"
            />
            <YoutubeVideo
              v-else
              :aria-label="videoAriaLabel"
              :class="BIG_ITEM_SIZE_CLASS"
              :img-class-name="BIG_ITEM_SIZE_CLASS"
              :is-mobile
              @start="onVisibleImage(index, 'video')"
            />
          </ExtraItem>
        </template>
      </template>
    </Carousel>
    <RevMediaViewer
      v-if="allowMediaViewer"
      :alternative-button-label="i18n(translations.alternativeController)"
      :alternative-next="i18n(translations.alternativeNext)"
      :alternative-previous="i18n(translations.alternativePrevious)"
      :close-button-label="i18n(translations.alternativeClose)"
      :current-index="mediaViewerIndex || 0"
      current-index-id="gallery-media-viewer"
      data-test="product-page-media-viewer"
      :name="MODAL_NAMES.MEDIA_VIEWER"
      :thumbnails
      @close="onMediaViewerClose"
    >
      <template #default="{ imgClassNames = '' } = {}">
        <RevIllustration
          v-for="image in modalImages"
          :key="image.alt"
          :alt="image.alt"
          :class="imgClassNames"
          decoding="async"
          :height="1200"
          loading="lazy"
          :src="image.src"
          :width="1200"
        />
      </template>
    </RevMediaViewer>
  </div>
</template>

<script lang="ts" setup>
import { useHead } from '#imports'
import {
  computed,
  onUnmounted,
  ref,
  shallowRef,
  useTemplateRef,
  watch,
} from 'vue'

import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import { RevIllustration } from '@ds/components/Illustration'
import { RevMediaViewer } from '@ds/components/MediaViewer'
import { openModal } from '@ds/components/ModalBase'
import { IconMediaPlay } from '@ds/icons/IconMediaPlay'
import { useIllustrationOptimizer } from '@ds/plugin'
import { css } from '@ds/utils/css'

import marketingVideoTranslations from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryVideoItem/components/MarketingVideo/MarketingVideo.translations'
import MarketingVideo from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryVideoItem/components/MarketingVideo/MarketingVideo.vue'
import { MODAL_NAMES } from '~/scopes/product-core/constants'

import {
  BIG_ITEM_SIZE_CLASS,
  CAROUSEL_MAX_HEIGHT_CLASS,
  MAIN_IMG_SIZES,
  TRACKING_EXTRA_IMAGE_TYPE,
} from './GalleryNew.constants'
import translations from './GalleryNew.translations'
import type {
  GalleryImage,
  GalleryImageExtraItemType,
  ReviewWithCount,
} from './GalleryNew.types'
import AltImageItem from './components/AltImageItem.vue'
import Carousel from './components/Carousel/Carousel.vue'
import ExtraItem from './components/ExtraItem.vue'
import MainImageContainer from './components/MainImageContainer.vue'
import Review from './components/Review/Review.vue'
import YoutubeVideo from './components/YoutubeVideo/YoutubeVideo.vue'
import { useExtraItems } from './composables/useExtraItems'

const {
  images,
  productType,
  trackingProductModel,
  allowMediaViewer = true,
  allowPartnersImages = true,
  shouldPreloadFirstImg = false,
  review,
  withSticker = true,
} = defineProps<{
  images: GetProductResponse['images']
  allowMediaViewer?: boolean
  allowPartnersImages?: boolean
  trackingProductModel: string
  shouldPreloadFirstImg?: boolean
  productType: GetProductResponse['type']
  review?: ReviewWithCount | null
  withSticker?: boolean
}>()

const emit = defineEmits<{
  (e: 'sticker-click'): void
}>()

const i18n = useI18n()
const { trackClick, trackCarouselImpression } = useTracking()

const videoAriaLabel = computed(() =>
  trackingProductModel
    ? i18n(marketingVideoTranslations.playAriaLabel, {
        productName: trackingProductModel,
      })
    : undefined,
)

const reviewAriaLabel = computed(() =>
  trackingProductModel
    ? i18n(translations.reviewButtonAriaLabel, {
        productName: trackingProductModel,
      })
    : undefined,
)

const shouldDisplaySticker = computed(
  () => withSticker && productType === 'Refurbished',
)

// Define the ref first so we can use it in defineExpose
const mainImageContainer = useTemplateRef('mainImageContainer')

const shouldDisplayGallery = computed(() => images.length > 0)

const baseCarouselImages = ref(
  images
    .filter((image) => allowPartnersImages || image.source !== 'PARTNER')
    .map((image, index) => ({
      id: `base-${index}-${image.url}`,
      src: image.url,
      alt: image.description,
    })),
)

const { imagesListWithExtraItems, extraItemsIndexes, videoSource } =
  useExtraItems({
    review,
    baseCarouselImages,
  })

const modalImages = computed(() => {
  return imagesListWithExtraItems.value.filter(
    (image) => !image.isExtra || image.type === 'jpRefurbishmentInfographic',
  )
})

const thumbnails = computed(() => {
  return modalImages.value.map((image) => ({
    ...image,
    width: 100,
    height: 100,
  }))
})

const optimizedImage = useIllustrationOptimizer(
  () => baseCarouselImages.value?.[0]?.src,
)

useHead({
  style: [
    {
      key: 'gallery-critical-styles',
      id: 'gallery-critical-styles',
      tagPriority: 'critical',
      textContent: css`
        :root {
          --carousel-max-size: 45lvh;

          --carousel-big-item-size: calc(80vw - 26px);
          --carousel-big-item-width: var(--carousel-big-item-size);
          --carousel-big-item-max-size: var(--carousel-max-size);

          --gap-between-images: 12px;
          /* Small items are only relevant on mobile */
          --carousel-small-item-size: calc(
            (var(--carousel-big-item-size) / 2) - var(--gap-between-images)
          );
          --carousel-small-item-max-size: calc(
            (var(--carousel-max-size) / 2) - var(--gap-between-images)
          );

          @media (min-width: 768px) {
            /* used both in the main image and the main image */
            --carousel-big-item-width: calc(80vw - 26px);
            /* fit in content or span to '--carousel-big-item-width' if possible */
            --carousel-big-item-size: 100%;
            --carousel-max-size: 448px;

            --carousel-small-item-size: 104px;
          }

          @media (min-width: 1200px) {
            /* min() keeps this fluid instead of a hard 448px so the main
               image can still shrink when its container is narrower than
               448px (e.g. the shopping-assistant squeeze), rather than
               forcing the row wider than the available space. */
            --carousel-big-item-size: min(448px, 100%);
          }
        }
      `,
    },
  ],
  link: shouldPreloadFirstImg
    ? [
        {
          rel: 'preload',
          href: optimizedImage.value.src,
          as: 'image',
          imagesrcset: optimizedImage.value.srcset,
          imagesizes: MAIN_IMG_SIZES,
        },
      ]
    : [],
})

// ================================================
// Tracking
// ================================================

const breakpoint = useClientOnlyComputedBreakpoint()

/**
 * Should not be used for UI purposes, only for tracking.
 */
const isMobile = computed(
  () =>
    breakpoint.value === Breakpoint.XS || breakpoint.value === Breakpoint.SM,
)

function getTrackingIndex(image: GalleryImage, indexInList: number): number {
  let trackingIndex = indexInList
  if (!isMobile.value) {
    // Order in images list directly dictates the display order for small screens.
    // On larger screens, the ordering can be changed with 'getDesktopOrder' function.
    // This logic calculates the 0-based index of an image on desktop.
    if (indexInList === 0) {
      trackingIndex = 0
    } else if (image.isExtra) {
      // Given:
      // 0 1 2 3
      //   E   E
      // Returns tracking index:
      //   1   2
      const countExtraBefore = imagesListWithExtraItems.value
        .slice(1, indexInList)
        .filter((img) => img.isExtra).length
      trackingIndex = countExtraBefore + 1
    } else {
      const countExtraBefore = imagesListWithExtraItems.value
        .slice(1, indexInList)
        .filter((img) => img.isExtra).length
      const countExtraTotal = imagesListWithExtraItems.value.filter(
        (img) => img.isExtra,
      ).length
      // Given:
      // 0 1 2 3 4 5 6
      //     E     E
      // Expected tracking index:
      //   3   4 5   6
      trackingIndex = indexInList - countExtraBefore + countExtraTotal
    }
  }

  return trackingIndex
}

// only tracks each image impression once (via useEmitOnce + IntersectionObserver)
function onVisibleImage(index: number, imageType?: GalleryImageExtraItemType) {
  const image = imagesListWithExtraItems.value[index]
  const trackingIndex = getTrackingIndex(image, index)

  // Skip tracking for images visible without having scrolled in mobile
  if (isMobile.value && (trackingIndex === 1 || trackingIndex === 2)) {
    return
  }

  trackCarouselImpression({
    name: 'product',
    zone: 'pp_carousel',
    currentSlide: trackingIndex,
    value: `${trackingIndex + 1}/${imagesListWithExtraItems.value.length}`,
    imageType:
      image.isExtra && imageType
        ? TRACKING_EXTRA_IMAGE_TYPE[imageType]
        : 'product_image',
  })
}

function onScrollStart(): void {
  trackClick({
    name: 'scroll_start',
    zone: 'pp_content_carousel',
    product_model: trackingProductModel ?? '',
  })
}

let scrollEndTimeout: ReturnType<typeof setTimeout> | null = null
function onScrollEnd(): void {
  if (scrollEndTimeout) {
    clearTimeout(scrollEndTimeout)
  }
  // This should be triggered after all the images have been loaded
  // and all 'carousel_impression' events have been emitted, so
  // we can trigger the 'scroll_end' event after a small delay.
  scrollEndTimeout = setTimeout(
    () => {
      trackClick({
        name: 'scroll_end',
        zone: 'pp_content_carousel',
        product_model: trackingProductModel ?? '',
      })
    },
    (imagesListWithExtraItems.value.length + 1) * 10,
  )
}

const isMediaViewerOpen = shallowRef(false)
const currentActiveIndex = shallowRef(0)
const mediaViewerIndex = shallowRef(currentActiveIndex.value)

/**
 * This function should only be called when the main image is clicked.
 * And when the media is not an non-image extra item.
 */
function openMediaViewer(image: GalleryImage): void {
  if (allowMediaViewer) {
    isMediaViewerOpen.value = true

    // Media Viewer modal only has `modalImages'
    const indexFromModalImages = modalImages.value.findIndex(
      (i) => i.src === image.src,
    )
    mediaViewerIndex.value = indexFromModalImages

    trackClick({
      name: 'photo_carousel',
      zone: 'pp_content_carousel',
      product_model: trackingProductModel ?? '',
      currentSlide: indexFromModalImages,
      type:
        image.isExtra && image.type
          ? TRACKING_EXTRA_IMAGE_TYPE[image.type]
          : 'product_image',
    })

    openModal(MODAL_NAMES.MEDIA_VIEWER)
  }
}

function onMediaViewerClose(): void {
  isMediaViewerOpen.value = false
  mediaViewerIndex.value = 0
}

// Extra items handling
// ================================================

function trackExtraItemClick(type: GalleryImageExtraItemType): void {
  if (type === 'review' && review) {
    trackClick({
      name: 'go_to_reviews',
      page_type: 'product',
      value: {
        average_rate: review.averageRate,
        count: review.count,
      },
      zone: 'image_carousel',
    })
  }
}

function onEnterAltImage(index: number): void {
  if (!isMobile.value) {
    currentActiveIndex.value = index
  }
}

function handleAltImageItemClick(
  index: number,
  imageType?: GalleryImageExtraItemType,
): void {
  if (
    (!imageType || imageType === 'jpRefurbishmentInfographic') &&
    isMobile.value
  ) {
    openMediaViewer(imagesListWithExtraItems.value[index])
  }

  if (imageType) {
    trackExtraItemClick(imageType)

    if (isMobile.value) {
      if (imageType === 'review' && review) {
        const reviewCardRef = mainImageContainer.value?.reviewCard
        if (reviewCardRef) {
          const reviewCard = Array.isArray(reviewCardRef)
            ? reviewCardRef[0]
            : reviewCardRef
          if (reviewCard && typeof reviewCard.openModal === 'function') {
            reviewCard.openModal()
          }
        }
      }

      if (imageType === 'video') {
        const videoCardRef = mainImageContainer.value?.videoCard
        if (videoCardRef) {
          const videoCard = Array.isArray(videoCardRef)
            ? videoCardRef[0]
            : videoCardRef
          if (videoCard && typeof videoCard.playVideo === 'function') {
            videoCard.playVideo()
          }
        }
      }
    }
  }
}

function getAltImageEvents(
  image:
    | {
        src: string
        alt: string
      }
    | {
        src: string
        alt: string
        isExtra: true
        type: GalleryImageExtraItemType
      },
  index: number,
): Record<string, unknown> {
  return {
    onVisible: () =>
      onVisibleImage(index, 'isExtra' in image ? image.type : undefined),
    onEnter: () => onEnterAltImage(index),
    onClick: () =>
      handleAltImageItemClick(
        index,
        'isExtra' in image ? image.type : undefined,
      ),
  }
}

// Reset the active index when the window is resized to mobile
// This way, the main image is the first one (like when you first load the page)
watch(isMobile, (isMobileNow, wasMobile) => {
  if (isMobileNow && !wasMobile) {
    currentActiveIndex.value = 0
    mediaViewerIndex.value = 0
  }
})

onUnmounted(() => {
  if (scrollEndTimeout) {
    clearTimeout(scrollEndTimeout)
  }
})
</script>
